import * as THREE from 'three'

const { themeUrl = '' } = window.psdigital ?? {}

let wearMap = null

/** A photo of a worn black sleeve (pale = board showing through), shared; the crop trims the backdrop. */
function loadWearMap() {
	if (!wearMap) {
		wearMap = new THREE.TextureLoader().load(`${themeUrl}/static/textures/record_wear_alt.jpg`)
		wearMap.repeat.set(0.945, 0.944)
		wearMap.offset.set(0.028, 0.031)
		wearMap.wrapS = wearMap.wrapT = THREE.ClampToEdgeWrapping
	}
	return wearMap
}

/**
 * MeshPhysicalMaterial for the sleeve: the cover art (as emissive, so it's unlit) under a
 * gloss laminate. Patched in: rounded corners, a grade on the art, and the wear photo.
 */
export default class SleeveMaterial extends THREE.MeshPhysicalMaterial {
	constructor(map) {
		super({
			color: 0x000000,
			emissive: 0xffffff,
			emissiveMap: map,
			metalness: 0.8,
			roughness: 0.3,
			roughnessMap: loadWearMap(),
			transparent: true,
		})

		this.sleeve = {
			uWear: { value: 1 },
		}
	}

	onBeforeCompile(shader) {
		Object.assign(shader.uniforms, this.sleeve)

		shader.vertexShader = shader.vertexShader
			.replace('#include <common>', 'varying vec2 vLocal;\n#include <common>')
			.replace('#include <uv_vertex>', '#include <uv_vertex>\nvLocal = uv;')

		shader.fragmentShader = shader.fragmentShader
			.replace(
				'#include <common>',
				`uniform float uWear;
				varying vec2 vLocal; // untransformed uv, 0..1 across the plane
				const float RADIUS = 0.0025; // corner radius as a fraction of the sleeve size
				const float SATURATION = 0.8; // a little punchier than the source image, 1 = as is
				const float CONTRAST = 1.05; // stretched about mid-grey, 1 = as is
				#include <common>
				// signed distance to a rounded box centred on the plane
				float roundedBox(vec2 p, vec2 extent, float r) {
					vec2 q = abs(p) - extent + r;
					return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
				}`
			)
			// the photo's brightness is the wear, lifted off its dark-grey base
			.replace(
				'#include <roughnessmap_fragment>',
				`float wear = smoothstep(0.06, 0.45, texture2D(roughnessMap, vRoughnessMapUv).g) * uWear;
				float roughnessFactor = mix(roughness, 1.0, wear);`
			)
			.replace(
				'#include <emissivemap_fragment>',
				`float dist = roundedBox(vLocal - 0.5, vec2(0.5), RADIUS);
				float alpha = 1.0 - smoothstep(-fwidth(dist), 0.0, dist);
				if (alpha <= 0.0) discard;
				diffuseColor.a *= alpha;
				vec2 artUv = (vEmissiveMapUv - 0.5) / 1. + 0.5;
				vec3 art = texture2D(emissiveMap, artUv).rgb;
				art = mix(vec3(dot(art, vec3(0.2126, 0.7152, 0.0722))), art, SATURATION);
				art = clamp((art - 0.5) * CONTRAST + 0.5, 0.0, 1.0);
				vec3 card = mix(art, vec3(0.85), 0.7); // the pale board under the print
				totalEmissiveRadiance *= mix(art, card, wear);`
			)
	}
}
